'use client';

import { useState } from 'react';
import Link from 'next/link';
import { usePathname, useRouter } from 'next/navigation';
import {
  ChevronDown,
  Headphones,
  LogOut,
  LucideIcon,
  Menu,
  Sparkles,
  Star,
  StarOff,
} from 'lucide-react';

import { BALLEE_USER_ID } from '@kit/chat/lib';
import { useSignOut } from '@kit/supabase/hooks/use-sign-out';
import { Button } from '@kit/ui/button';
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from '@kit/ui/collapsible';
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
} from '@kit/ui/sheet';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@kit/ui/tooltip';
import { Trans } from '@kit/ui/trans';
import { cn, isRouteActive } from '@kit/ui/utils';

import { AppLogo } from '~/components/app-logo';
import { BrandLink } from '~/components/brand-link';
import { LocaleSelector } from '~/components/locale-selector';
import { consolidatedAccountSections } from '~/config/navigation/nav-sections';
import type { NavSection } from '~/config/navigation/nav-types';
import { NavNewBadge } from '~/config/navigation/to-nav-config';
import { getResolvedNavSections } from '~/config/personal-account-navigation.config';
import type { Brand } from '~/lib/brand';
import { isActingSuperAdmin, resolveActingRole } from '../_lib/acting-role';
// home imports
import type { UserWorkspace } from '../_lib/server/load-user-workspace';
import {
  adminNavigationGroups,
  adminNavItemsByPath,
} from './admin-tab-context';
import { BrandSwitcherWidget } from './brand-switcher-widget';
import { useFavoritesOptional } from './sidebar-favorites';
import { WorkspaceSwitcher } from './workspace-switcher';

// Navigation item definition for favorites
interface NavItem {
  label: string;
  path: string;
  icon: LucideIcon;
}

// navItemsMap + adminGroups = single source of truth in admin-tab-context.tsx (Rule #8)
const navItemsMap = adminNavItemsByPath;

// Admin navigation groups (rendered by the adminTools section, both brands)
const adminGroups = adminNavigationGroups;

// Check if any item in a group is active
function hasActiveChild(
  items: { path: string; end?: boolean }[],
  pathname: string,
): boolean {
  return items.some((item) => isRouteActive(item.path, pathname, item.end));
}

export function HomeMobileNavigation(props: {
  workspace: UserWorkspace;
  brand?: Brand;
  /** Explicit `lang` cookie choice from the server layout; `null` = Automatic. */
  selectedLocale: string | null;
  /**
   * Custom sheet trigger. The default is the hamburger icon button used in
   * the top mobile row; the bottom tab bar passes its own "More" tab so the
   * same menu sheet opens from either surface.
   */
  trigger?: React.ReactNode;
}) {
  const [open, setOpen] = useState(false);
  const signOut = useSignOut();
  const pathname = usePathname();
  const router = useRouter();
  const favoritesContext = useFavoritesOptional();

  // The menu follows the ACTING role (BAL-340): a super admin acting as
  // Dancer gets the plain dancer menu, no admin sections; acting as Client
  // gets only the client portal. Mirrors home-sidebar.tsx.
  const actingContext = {
    isSuperAdmin: props.workspace.isSuperAdmin,
    isClientUser: props.workspace.isClientUser,
    activeRole: props.workspace.activeRole,
    roles: props.workspace.roles,
  };
  const actingRole = resolveActingRole(actingContext);
  const isSuperAdmin = isActingSuperAdmin(actingContext);
  // Mobile analog of the desktop WorkspaceSwitcher: the client accounts the
  // user belongs to (switch context) + the team-account workspaces they
  // administer (navigate). Shown regardless of dominant nav role.
  const orgAdminAccounts = props.workspace.orgAdminAccounts ?? [];
  const clientMemberships = props.workspace.clientMemberships ?? [];
  const currentClientId = props.workspace.clientId ?? null;

  // Use context for favorites (synced with desktop sidebar)
  const favorites = favoritesContext?.favorites ?? [];
  const toggleFavorite = favoritesContext?.toggleFavorite;
  const isFavorite = favoritesContext?.isFavorite ?? (() => false);
  const canAddMore = favoritesContext?.canAddMore ?? true;

  // Acting as Admin → admin-only sections (no user surfaces); acting as
  // Client → only the client portal; acting as any other user role → neither
  // admin sections nor the client portal. Mirrors home-sidebar.tsx.
  const sections = getResolvedNavSections(
    isSuperAdmin
      ? {
          isSuperAdmin: true,
          isClientUser: false,
          isDancer: false,
          isOrganizer: false,
          isSeller: false,
          hasPerformanceData: false,
          needsProfileSetup: false,
          clientRole: null,
          brand: props.brand,
          partnerCompetitions: [],
          dancerProfileSlug: null,
        }
      : actingRole === 'client'
        ? {
            isSuperAdmin: false,
            isClientUser: true,
            isDancer: false,
            isOrganizer: false,
            isSeller: false,
            hasPerformanceData: false,
            needsProfileSetup: false,
            clientRole: props.workspace.clientRole,
            brand: props.brand,
            partnerCompetitions: [],
            dancerProfileSlug: null,
          }
        : props.workspace.isSuperAdmin && actingRole === 'dancer'
          ? {
              // Super-admin dancer PREVIEW — a clean dancer menu. MUST gate on
              // the REAL isSuperAdmin (props.workspace, not the acting flag):
              // resolveActingRole returns a regular dancer's own 'dancer' role,
              // so without this gate every dancer loses their composed nav.
              // Mirrors home-sidebar.tsx; switcher offers 'dancer' to admins only.
              isSuperAdmin: false,
              isClientUser: false,
              isDancer: true,
              isOrganizer: false,
              isSeller: false,
              hasPerformanceData: false,
              needsProfileSetup: false,
              clientRole: null,
              brand: props.brand,
              partnerCompetitions: [],
              dancerProfileSlug: props.workspace.dancerProfileSlug,
            }
          : {
              isSuperAdmin: false,
              isClientUser: false,
              isDancer: props.workspace.isDancer,
              isOrganizer: props.workspace.isOrganizer,
              isSeller: props.workspace.isSeller,
              hasPerformanceData: props.workspace.hasPerformanceData,
              needsProfileSetup: props.workspace.needsProfileSetup,
              // BAL-668: mirrors the desktop sidebar — surfaces the "membership
              // request pending" notice for a role-less partner/staff awaiting
              // org-admin approval.
              hasPendingOrgJoinRequest:
                props.workspace.hasPendingOrgJoinRequest,
              clientRole: props.workspace.clientRole,
              brand: props.brand,
              partnerCompetitions: props.workspace.partnerCompetitions,
              dancerProfileSlug: props.workspace.dancerProfileSlug,
            },
  );

  // My Profile + Account moved out of the sidebar sections (2026-07-05
  // consolidation) — the mobile sheet appends them after the work surfaces,
  // collapsed by default, sharing the same builder as the footer dropdown.
  const consolidatedGroups: NavSection[] = consolidatedAccountSections(
    {
      isSuperAdmin,
      isClientUser: props.workspace.isClientUser,
      isDancer: props.workspace.isDancer,
      needsProfileSetup: props.workspace.needsProfileSetup,
      hasPerformanceData: props.workspace.hasPerformanceData,
      clientRole: props.workspace.clientRole,
      dancerProfileSlug: props.workspace.dancerProfileSlug,
    },
    props.brand,
  ).map((section) => ({ ...section, defaultOpen: false }));

  const handleLinkClick = () => {
    setOpen(false);
  };

  // Check if any child in a section is active
  const isSectionActive = (children: { path: string }[]) => {
    return children.some(
      (child) =>
        child.path === pathname ||
        (pathname.startsWith(child.path) && child.path !== '/home'),
    );
  };

  // Get favorite items
  const favoriteItems: NavItem[] = favorites
    .map((path) => navItemsMap[path])
    .filter((item): item is NavItem => item !== undefined);

  // ── Section renderers (dispatch only on section.kind) ────────────────────

  const renderFavorites = (key: string) => {
    if (favoriteItems.length === 0) return null;
    return (
      <div key={key} className="px-3 py-2">
        <div className="text-muted-foreground mb-2 flex items-center gap-1 text-xs font-medium">
          <Star className="h-3 w-3" />
          <span>
            <Trans i18nKey="home:nav.favorites" />
          </span>
        </div>
        <div className="space-y-1">
          {favoriteItems.map((item) => {
            const isActive =
              item.path === pathname ||
              (pathname.startsWith(item.path) && item.path !== '/admin');
            const Icon = item.icon;

            return (
              <BrandLink
                key={item.path}
                to={item.path}
                onClick={handleLinkClick}
                className={cn(
                  'flex h-10 items-center gap-3 rounded-md px-3 text-sm transition-colors',
                  isActive
                    ? 'bg-accent text-accent-foreground font-medium'
                    : 'text-muted-foreground hover:bg-accent hover:text-accent-foreground',
                )}
              >
                <Icon className="h-4 w-4" />
                <span>{item.label}</span>
              </BrandLink>
            );
          })}
        </div>
      </div>
    );
  };

  const renderAdminTools = (key: string) => (
    <div key={key} className="px-3 py-2">
      {adminGroups.map((group) => {
        const hasActive = hasActiveChild(group.items, pathname);

        return (
          <Collapsible
            key={group.label}
            defaultOpen={hasActive}
            className="mb-2"
          >
            <CollapsibleTrigger className="hover:bg-accent/50 flex h-10 w-full items-center justify-between rounded-md px-3 text-sm font-semibold transition-colors">
              <span>{group.label}</span>
              <ChevronDown className="h-4 w-4 shrink-0 transition-transform duration-200 [[data-state=open]>&]:rotate-180" />
            </CollapsibleTrigger>
            <CollapsibleContent className="mt-1 space-y-1 pl-2">
              {group.items.map((item) => {
                const isActive = isRouteActive(item.path, pathname, item.end);
                const Icon = item.icon;
                const itemIsFavorite = isFavorite(item.path);

                return (
                  <div
                    key={item.path}
                    className="group/nav-item relative flex items-center"
                  >
                    <BrandLink
                      to={item.path}
                      onClick={handleLinkClick}
                      className={cn(
                        'flex h-10 flex-1 items-center gap-3 rounded-md px-3 text-sm transition-colors',
                        isActive
                          ? 'bg-accent text-accent-foreground font-medium'
                          : 'text-muted-foreground hover:bg-accent hover:text-accent-foreground',
                      )}
                    >
                      <Icon className="h-4 w-4" />
                      <span>{item.label}</span>
                    </BrandLink>
                    <TooltipProvider delayDuration={0}>
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <Button
                            variant="ghost"
                            size="icon"
                            className={cn(
                              'h-8 w-8 shrink-0 opacity-0 transition-opacity group-hover/nav-item:opacity-100',
                              itemIsFavorite && 'text-yellow-500 opacity-100',
                            )}
                            onClick={(e) => {
                              e.preventDefault();
                              e.stopPropagation();
                              toggleFavorite?.(item.path);
                            }}
                            disabled={
                              !toggleFavorite ||
                              (!itemIsFavorite && !canAddMore)
                            }
                          >
                            {itemIsFavorite ? (
                              <StarOff className="h-4 w-4" />
                            ) : (
                              <Star className="h-4 w-4" />
                            )}
                          </Button>
                        </TooltipTrigger>
                        <TooltipContent side="right">
                          {itemIsFavorite
                            ? 'Remove from favorites'
                            : canAddMore
                              ? 'Add to favorites'
                              : 'Maximum 5 favorites reached'}
                        </TooltipContent>
                      </Tooltip>
                    </TooltipProvider>
                  </div>
                );
              })}
            </CollapsibleContent>
          </Collapsible>
        );
      })}
    </div>
  );

  const renderGroup = (
    section: Extract<NavSection, { kind: 'group' }>,
    key: string,
  ) => {
    // Single-leaf groups whose label repeats their only child render bare:
    // the item stands alone, no group label / collapse chrome
    // (navigation-architecture rule). Mirrors the desktop SidebarNavigation.
    const bareSingleLeaf =
      section.items.length === 1 &&
      section.items[0]?.i18nKey === section.i18nKey;
    const isCollapsible = (section.collapsible ?? false) && !bareSingleLeaf;
    const children = section.items.map((item) => ({
      path: item.path,
      label: item.i18nKey,
      Icon: <item.icon className="h-4 w-4" />,
      isNew: item.isNew ?? false,
    }));
    const hasActiveChildRoute = isSectionActive(children);
    const defaultOpen =
      !isCollapsible || hasActiveChildRoute || (section.defaultOpen ?? true);

    const sectionContent = (
      <div className="space-y-1">
        {children.map((child) => {
          const isActive =
            child.path === pathname ||
            (pathname.startsWith(child.path) && child.path !== '/home');

          return (
            <Link
              key={child.path}
              href={child.path}
              onClick={handleLinkClick}
              className={cn(
                'flex h-10 items-center gap-3 rounded-md px-3 text-sm transition-colors',
                isActive
                  ? 'bg-accent text-accent-foreground font-medium'
                  : 'text-muted-foreground hover:bg-accent hover:text-accent-foreground',
              )}
            >
              {child.Icon}
              <span>
                <Trans i18nKey={child.label} defaults={child.label} />
              </span>
              {child.isNew ? <NavNewBadge /> : null}
            </Link>
          );
        })}
      </div>
    );

    if (isCollapsible) {
      return (
        <Collapsible key={key} defaultOpen={defaultOpen} className="mb-2">
          <CollapsibleTrigger className="hover:bg-accent/50 flex h-10 w-full items-center justify-between rounded-md px-3 text-sm font-semibold transition-colors">
            <Trans i18nKey={section.i18nKey} defaults={section.i18nKey} />
            <ChevronDown className="h-4 w-4 shrink-0 transition-transform duration-200 [[data-state=open]>&]:rotate-180" />
          </CollapsibleTrigger>
          <CollapsibleContent className="mt-1 pl-2">
            {sectionContent}
          </CollapsibleContent>
        </Collapsible>
      );
    }

    if (bareSingleLeaf) {
      return (
        <div key={key} className="mb-2">
          {sectionContent}
        </div>
      );
    }

    return (
      <div key={key} className="mb-4">
        <span className="text-muted-foreground mb-2 block px-3 text-xs font-medium tracking-wider uppercase">
          <Trans i18nKey={section.i18nKey} defaults={section.i18nKey} />
        </span>
        {sectionContent}
      </div>
    );
  };

  return (
    <Sheet open={open} onOpenChange={setOpen}>
      <SheetTrigger asChild>
        {props.trigger ?? (
          <Button variant="ghost" size="icon" className="h-9 w-9">
            <Menu className="h-6 w-6" />
            <span className="sr-only">
              <Trans i18nKey="common:accessibility.menu" defaults="Menu" />
            </span>
          </Button>
        )}
      </SheetTrigger>

      <SheetContent side="left" className="flex w-80 flex-col p-0">
        <SheetHeader className="px-4 pt-4 pb-2">
          <SheetTitle className="text-left">
            <AppLogo />
          </SheetTitle>
        </SheetHeader>

        {/* Workspaces — the SAME WorkspaceSwitcher as the desktop top bar, in
            one compact slot (replaced the flat Clients/Organizations lists
            that ballooned the sheet, 2026-07-06). The current-client
            checkmark and label are gated on the ACTING role, so a personal
            session never shows a stored client (e.g. Fever Poland) as the
            active workspace — mirrors the desktop label fix. */}
        {(clientMemberships.length > 0 ||
          orgAdminAccounts.length > 0 ||
          isSuperAdmin) && (
          <div className="px-3 pb-1">
            <WorkspaceSwitcher
              orgAdminAccounts={orgAdminAccounts}
              clientMemberships={clientMemberships}
              currentClientId={actingRole === 'client' ? currentClientId : null}
              currentLabel={
                actingRole === 'client'
                  ? (clientMemberships.find(
                      (c) => c.clientId === currentClientId,
                    )?.clientName ??
                    props.workspace.workspace.name ??
                    '')
                  : (props.workspace.workspace.name ?? '')
              }
              enableAdminSearch={isSuperAdmin}
              onNavigate={() => setOpen(false)}
              triggerClassName="h-12 w-full"
            />
          </div>
        )}

        {/* One walker over the resolved sections — dispatch on section.kind,
            identical for every role and both brands. */}
        <nav className="flex-1 overflow-y-auto px-3 py-4">
          {sections.map((section, index) => {
            if (section.kind === 'divider') {
              return (
                <div key={`divider-${index}`} className="bg-border my-3 h-px" />
              );
            }
            if (section.kind === 'favorites') {
              return renderFavorites(`favorites-${index}`);
            }
            if (section.kind === 'adminTools') {
              return renderAdminTools(`admin-tools-${index}`);
            }
            return renderGroup(section, `group-${index}`);
          })}

          {consolidatedGroups.map((section, index) =>
            section.kind === 'group'
              ? renderGroup(section, `consolidated-${index}`)
              : null,
          )}

          {/* Cross-brand Flow card lives at the END of the scrollable nav (not
              the fixed footer) so it stops shrinking the scroll area. Hidden for
              real client users who operate on Flow only; super admins are
              flagged isClientUser for client-portal preview, so re-include them
              via the REAL super-admin flag (matches the desktop sidebar). */}
          {(!props.workspace.isClientUser || props.workspace.isSuperAdmin) && (
            <div className="mt-4">
              <BrandSwitcherWidget
                currentBrand={props.brand ?? 'flow'}
                variant="card"
              />
            </div>
          )}
        </nav>

        {/* Footer utilities — Need help, Ask Flow (super admins), Sign out.
            One subtle top border separates them from the scrollable nav; no
            per-row dividers (X/Nextdoor drawer pattern). */}
        <div className="border-border/60 mt-auto space-y-1 border-t p-3">
          {/* Language — same row metrics as the utility buttons below; the
              selector reloads the page on change, which also closes the sheet. */}
          <LocaleSelector
            variant="footer"
            selectedLocale={props.selectedLocale}
            className="h-10 w-full justify-start gap-3 rounded-md px-3 text-sm"
          />

          <Button
            type="button"
            variant="ghost"
            onClick={() => {
              router.push('/home/support');
              setOpen(false);
            }}
            className="text-muted-foreground hover:bg-accent hover:text-accent-foreground flex h-10 w-full items-center justify-start gap-3 rounded-md px-3 text-sm transition-colors"
          >
            <Headphones className="h-4 w-4" />
            <span>
              <Trans
                i18nKey="home:headerActions.getHelp"
                defaults="Need help"
              />
            </span>
          </Button>

          {/* Flow AI Assistant - Flow super admins only (inbox is not on Ballee) */}
          {isSuperAdmin && props.brand !== 'ballee' && (
            <Button
              type="button"
              variant="ghost"
              onClick={() => {
                router.push(`/home/inbox?user=${BALLEE_USER_ID}`);
                setOpen(false);
              }}
              className="text-muted-foreground hover:bg-accent hover:text-accent-foreground flex h-10 w-full items-center justify-start gap-3 rounded-md px-3 text-sm transition-colors"
            >
              <Sparkles className="h-4 w-4" />
              <span>
                <Trans i18nKey="common:actions.askBallee" defaults="Ask Flow" />
              </span>
            </Button>
          )}

          <Button
            type="button"
            variant="ghost"
            onClick={() => signOut.mutateAsync()}
            className="text-muted-foreground hover:bg-accent hover:text-accent-foreground flex h-10 w-full items-center justify-start gap-3 rounded-md px-3 text-sm transition-colors"
          >
            <LogOut className="h-4 w-4" />
            <span>
              <Trans i18nKey="common:signOut" defaults="Sign out" />
            </span>
          </Button>
        </div>
      </SheetContent>
    </Sheet>
  );
}
